import type { Stock } from '@/scopes/prices/domain/rooms/types'
import { PricesFilterType } from '@/scopes/prices/domain/filters/types'
import { PricesState } from '@/scopes/prices/infrastructure/store'
import { format } from 'date-fns'

export function getFiltersSelector(state: PricesState) {
  return {
    loading: state.getFilters.loading,
    error: state.getFilters.error,
    isEmpty: state.getFilters.isEmpty,
  }
}

export function durationFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.Duration?.options ?? []
  const selectedValues = state.filterSelection.Duration ?? []
  return {
    options,
    selectedValues,
  }
}

export function roomTypeFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.RoomType?.options ?? []
  const selectedValues = state.filterSelection.RoomType ?? []
  return {
    options,
    selectedValues,
  }
}

export function transportTypeFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.TransportType?.options ?? []
  const selectedValue = state.filterSelection.TransportType ?? null
  return {
    options,
    selectedValue,
  }
}

export function mealplanFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.Mealplan?.options ?? []
  const selectedValue = state.filterSelection.Mealplan ?? null
  return {
    options,
    selectedValue,
  }
}

export function monthFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.Month?.options ?? []
  const selectedValue = state.filterSelection.Month ?? ''
  return {
    options,
    selectedValue,
  }
}

export function departureAirportSelector(state: PricesState) {
  const options = state.filterOptions.DepartureAirport?.options ?? []
  const selectedValues = state.filterSelection.DepartureAirport ?? []
  return {
    options,
    selectedValues,
  }
}

export function participantsSelector(state: PricesState) {
  const selectedValues = state.filterSelection.Participants ?? []
  return {
    selectedValues,
  }
}

export function getPricesSelector(state: PricesState) {
  return {
    loading: state.getPrices.loading,
    error: state.getPrices.error,
  }
}

export function pricesResponseSelector(state: PricesState) {
  return { ...state.pricesResponse }
}

export function alternativeFilterSelector(state: PricesState, filter: PricesFilterType) {
  return state.pricesResponse.alternativeFilters?.[filter] ?? null
}

export function selectedPriceIdSelector(state: PricesState) {
  return state.selectedPriceId
}

export function getRoomsSelector(state: PricesState) {
  return {
    loading: state.getRooms.loading,
    error: state.getRooms.error,
  }
}

export function getRoomsResponseSelector(state: PricesState) {
  return { ...state.roomsResponse }
}

export function selectedRoomInfosSelector(state: PricesState) {
  return { selectedRoomInfos: state.selectedRoomInfos }
}

function calculateUpdatedStock(currentStock: Stock, currentSelectedRooms: Record<string, string | null> | null): Stock {
  const updatedStock: Stock = { ...currentStock }

  for (const r in currentSelectedRooms) {
    const roomId = currentSelectedRooms[r]
    if (roomId && updatedStock[roomId] !== undefined) {
      updatedStock[roomId] -= 1
    }
  }
  return updatedStock
}

export function roomGroupSelector(state: PricesState, roomNumber: string) {
  const { roomGroups, stock } = getRoomsResponseSelector(state)
  const updatedStock = calculateUpdatedStock(stock, state.selectedRooms)

  const roomGroup = roomGroups?.find(group => group.roomNumber === roomNumber)

  return {
    rooms: roomGroup?.rooms || [],
    selectedRoom: state.selectedRooms?.[roomNumber],
    stock: updatedStock,
    distribution: roomGroup?.distribution,
  }
}

export function roomSelector(state: PricesState, roomNumber: string, roomId: string) {
  const { rooms, stock, selectedRoom } = roomGroupSelector(state, roomNumber)
  const { stock: apiStock } = getRoomsResponseSelector(state)
  const isSelected = selectedRoom === roomId

  return {
    room: rooms.find(room => room.id === roomId),
    stock: stock[roomId],
    apiStock: apiStock[roomId],
    isSelected,
  }
}

export function getTransportSelector(state: PricesState) {
  return {
    loading: state.getTransport.loading,
    error: state.getTransport.error,
  }
}

export function routesSelector(state: PricesState) {
  const { outboundRoutes, inboundRoutes, selectedOutboundRouteId, selectedInboundRouteId } = state
  const outboundRoute = outboundRoutes.find(route => route.id === selectedOutboundRouteId) || null
  const inboundRoute = inboundRoutes.find(route => route.id === selectedInboundRouteId) || null
  return {
    outboundRoutes,
    inboundRoutes,
    selectedOutboundRouteId,
    selectedInboundRouteId,
    outboundRoute,
    inboundRoute,
  }
}

export function transportResponseSelector(state: PricesState) {
  const { inboundRoute } = routesSelector(state)
  const transportResponseTripSummary = state.transportResponse?.tripSummary || []
  const selfDriveResponseTripSummary = state.selfDriveResponse?.tripSummary || []
  const tripSummary = [
    ...transportResponseTripSummary,
    ...(inboundRoute?.tripSummary || []),
    ...selfDriveResponseTripSummary,
  ]
  const priceBreakdown = inboundRoute?.priceBreakdown || state.selfDriveResponse?.priceBreakdown || null
  const paymentMethod = state.transportResponse?.paymentMethod || state.selfDriveResponse?.paymentMethod || null
  const transportType = state.transportResponse?.transportType || state.selfDriveResponse?.transportType
  return { tripSummary, priceBreakdown, paymentMethod, transportType }
}

export function createBookingDraftSelector(state: PricesState) {
  return {
    loading: state.createBookingDraft.loading,
    error: state.createBookingDraft.error,
    success: state.createBookingDraft.success,
  }
}

export function isPriceTableLoadingStateSelector(state: PricesState) {
  const loadingFilters = state.getFilters.loading
  const fetchingPrices = state.getPrices.loading
  const fetchingRooms = state.getRooms.loading
  const fetchingTransport = state.getTransport.loading
  const isBookingDraftCreationPending = state.createBookingDraft.loading
  const isBookingDraftCreationSuccess = state.createBookingDraft.success

  return loadingFilters
    || fetchingPrices
    || fetchingRooms
    || fetchingTransport
    || isBookingDraftCreationPending
    || isBookingDraftCreationSuccess
}

export function isAccommodationAvailableSelector(state: PricesState) {
  return {
    isAccommodationAvailable: state.isAccommodationAvailableResponse.isAccommodationAvailable,
    isFlightOnly: state.isAccommodationAvailableResponse.isFlightOnly,
    transportTypes: state.isAccommodationAvailableResponse.transportTypes,
    isFetching: state.isAccommodationAvailable.loading,
    success: state.isAccommodationAvailable.success,
  }
}

export function getPromotedPriceSelector(state: PricesState) {
  return {
    data: state.promotedPriceResponse,
    isError: state.getPromotedPrice.error,
    loading: state.getPromotedPrice.loading,
    success: state.getPromotedPrice.success,
  }
}

export function getPriceRangeSelector(state: PricesState) {
  const options = state.filterOptions.Month?.options ?? []

  const transformedOptions = options.reduce((acc, { value, name }) => {
    const [year] = value.split('-')
    if (!acc[year]) {
      acc[year] = []
    }
    acc[year].push({
      value,
      name,
    })
    return acc
  }, {} as Record<string, { value: string, name: string }[]>)

  return {
    dateFrom: state.filterSelection.DateFrom,
    dateTo: state.filterSelection.DateTo,
    options: transformedOptions,
    minDatePrices: state.pricesResponse.dates[0],
    maxDatePrices: state.pricesResponse.dates[state.pricesResponse.dates.length - 1],
    minPriceDate: state.pricesResponse.prices.sort((a, b) => a.minPricePerPerson - b.minPricePerPerson)[0]?.departureDate || '',
    selectedPriceDate: state?.selectedPriceId ? state.pricesResponse.prices.find(price => price.id === state.selectedPriceId)?.departureDate : '',
  }
}

export function isFirstMonthSelector(state: PricesState) {
  return {
    isFirstMonth: (date: string) => {
      const { options } = getPriceRangeSelector(state)
      const monthValues = Object.values(options).flat()
      if (monthValues.length === 0) {
        return false
      }
      return monthValues[0].value === format(new Date(date), 'yyyy-MM')
    },
    minDatePrices: state.pricesResponse.dates[0],
  }
}
